import { useState, useEffect, useMemo } from 'react';
import { X, Search, Music, Loader2, Shuffle, Tag } from 'lucide-react';
import {
  getMyLibrarySongs,
  searchLibrarySongs,
  type LibrarySong,
} from '../../services/songMatchMemory';
import { createRequest, isFuzzySongMatch, SongRequest } from '../../services/requests';
import type { PlayModeConfig } from '../../services/playModes';
import { libraryRequestData } from '../../utils/libraryRequest';
import { getMySongTags } from '../../services/songTags';
import { songsWithTag, tagCounts } from '../../utils/songTags';
import toast from 'react-hot-toast';

interface LibraryQueueModalProps {
  djUid: string;
  eventId: string | null;
  existingRequests: SongRequest[];
  playMode: PlayModeConfig;
  onSongsAdded: (songs: LibrarySong[]) => void;
  onClose: () => void;
}

export default function LibraryQueueModal({
  djUid,
  eventId,
  existingRequests,
  playMode,
  onSongsAdded,
  onClose,
}: LibraryQueueModalProps) {
  const [tab, setTab] = useState<'pick' | 'tag' | 'random'>('pick');
  const [allMatches, setAllMatches] = useState<LibrarySong[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [randomCount, setRandomCount] = useState(10);
  const [adding, setAdding] = useState(false);
  const [tagsBySong, setTagsBySong] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    if (!djUid) return;
    const librarySongsLoad = getMyLibrarySongs(djUid)
      .then(setAllMatches)
      .catch(err => {
        console.error('[LibraryQueueModal] Failed to load the link library:', err);
        toast.error('Failed to load match library');
      });
    const songTagsLoad = getMySongTags(djUid)
      .then(setTagsBySong)
      .catch(err => {
        console.error('[LibraryQueueModal] Failed to load song tags:', err);
        toast.error('Failed to load tags');
      });
    Promise.all([librarySongsLoad, songTagsLoad]).finally(() => setLoading(false));
  }, [djUid]);

  const tagSummary = useMemo(
    () => tagCounts(tagsBySong, new Set(allMatches.map(match => match.key))),
    [tagsBySong, allMatches],
  );

  const filteredMatches = useMemo(() => {
    return searchQuery.trim() ? searchLibrarySongs(allMatches, searchQuery) : allMatches;
  }, [allMatches, searchQuery]);

  function isDuplicate(match: LibrarySong): boolean {
    return existingRequests.some(r =>
      isFuzzySongMatch(match.title, match.artist, r.song.title, r.song.artist),
    );
  }

  function toggleKey(key: string, duplicate: boolean) {
    if (duplicate) return;
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function addMatchesToEvent(matches: LibrarySong[]) {
    if (!eventId) {
      onSongsAdded(matches);
      return;
    }

    let queuedCount = 0;
    let pendingCount = 0;
    let skippedCount = 0;
    let failedCount = 0;
    const addedMatches: LibrarySong[] = [];
    for (const match of matches) {
      const addedInThisBatch = addedMatches.some(added =>
        isFuzzySongMatch(match.title, match.artist, added.title, added.artist),
      );
      if (isDuplicate(match) || addedInThisBatch) {
        skippedCount++;
        continue;
      }
      try {
        const data = libraryRequestData(match, djUid, playMode);
        await createRequest(eventId, data);
        addedMatches.push(match);
        if (data.initialStatus === 'queued') queuedCount++;
        else pendingCount++;
      } catch (error) {
        failedCount++;
        console.error(`[LibraryQueueModal] Could not add "${match.title}":`, error);
      }
    }
    if (queuedCount > 0) {
      toast.success(`${queuedCount} song${queuedCount !== 1 ? 's' : ''} added to queue`);
    }
    if (pendingCount > 0) {
      toast(`${pendingCount} song${pendingCount !== 1 ? 's need' : ' needs'} a ${playMode.playerName} match — waiting in Pending`);
    }
    if (skippedCount > 0) {
      toast(`${skippedCount} already added — skipped`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} song${failedCount !== 1 ? 's' : ''} could not be added`);
    }
    onSongsAdded(addedMatches);
  }

  const handleAddSelected = async () => {
    const toAdd = allMatches.filter(m => selectedKeys.has(m.key));
    if (toAdd.length === 0) return;
    setAdding(true);
    try {
      await addMatchesToEvent(toAdd);
    } finally {
      setAdding(false);
    }
  };

  const handleAddTag = async (tag: string) => {
    setAdding(true);
    try {
      await addMatchesToEvent(songsWithTag(allMatches, tagsBySong, tag));
    } finally {
      setAdding(false);
    }
  };

  const handleAddRandom = async () => {
    const available = allMatches.filter(m => !isDuplicate(m));
    if (available.length === 0) {
      toast('No songs available to add');
      return;
    }
    const shuffled = [...available].sort(() => Math.random() - 0.5);
    const toAdd = shuffled.slice(0, Math.min(randomCount, shuffled.length));
    setAdding(true);
    try {
      await addMatchesToEvent(toAdd);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add from Library</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setTab('pick')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              tab === 'pick'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Pick Songs
          </button>
          <button
            onClick={() => setTab('tag')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              tab === 'tag'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            By Tag
          </button>
          <button
            onClick={() => setTab('random')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              tab === 'random'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Random Fill
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : tab === 'pick' ? (
          <>
            {/* Search */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by title or artist..."
                  className="input pl-9 w-full text-sm"
                />
              </div>
            </div>

            {/* Song list */}
            <div className="flex-1 overflow-y-auto">
              {filteredMatches.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Music className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">{searchQuery ? 'No matches found' : 'No songs in library'}</p>
                </div>
              ) : (
                filteredMatches.map(match => {
                  const key = match.key;
                  const duplicate = isDuplicate(match);
                  const selected = selectedKeys.has(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleKey(key, duplicate)}
                      disabled={duplicate}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${
                        duplicate
                          ? 'opacity-40 cursor-not-allowed'
                          : selected
                          ? 'bg-primary/10 hover:bg-primary/15'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                        selected ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selected && <span className="text-white text-xs font-bold">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{match.title}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {match.artist}
                          {duplicate && ' · already added'}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-500">{selectedKeys.size} selected</span>
              <button
                onClick={handleAddSelected}
                disabled={selectedKeys.size === 0 || adding}
                className="btn btn-primary text-sm disabled:opacity-50 flex items-center gap-1.5"
              >
                {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Add {selectedKeys.size > 0 ? `${selectedKeys.size} ` : ''}Song{selectedKeys.size !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        ) : tab === 'tag' ? (
          <div className="flex-1 overflow-y-auto">
            {tagSummary.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Tag className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No tags yet. Tag songs on the Match Library page.</p>
              </div>
            ) : (
              tagSummary.map(({ tag, count }) => {
                const taggedSongs = songsWithTag(allMatches, tagsBySong, tag);
                const duplicateCount = taggedSongs.filter(isDuplicate).length;
                const availableCount = taggedSongs.length - duplicateCount;
                return (
                  <div
                    key={tag}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{tag}</p>
                      <p className="text-xs text-gray-500">
                        {count} song{count !== 1 ? 's' : ''}
                        {duplicateCount > 0 && `, ${duplicateCount} already added`}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAddTag(tag)}
                      disabled={availableCount === 0 || adding}
                      className="btn btn-primary text-sm disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
                    >
                      {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Add {availableCount} song{availableCount !== 1 ? 's' : ''}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* Random Fill tab */
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6">
            <Shuffle className="w-10 h-10 text-primary opacity-60" />
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Add a random selection from your match library to the queue.
            </p>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700 dark:text-gray-300">Number of songs:</label>
              <input
                type="number"
                min={1}
                max={50}
                value={randomCount}
                onChange={e => setRandomCount(Math.max(1, Math.min(50, Number(e.target.value))))}
                className="input w-20 text-center text-sm"
              />
            </div>
            <button
              onClick={handleAddRandom}
              disabled={adding || allMatches.length === 0}
              className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50"
            >
              {adding && <Loader2 className="w-4 h-4 animate-spin" />}
              <Shuffle className="w-4 h-4" />
              Add {randomCount} Random Song{randomCount !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
